import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  HeartPulse,
  BarChart3,
  Flag,
  SlidersHorizontal,
  ShieldHalf,
  Sparkles,
  Tags,
  Inbox,
  AlertOctagon,
  Users,
  Trophy,
  Megaphone,
  ScrollText,
  Settings,
  BookOpen,
  X,
} from "lucide-react";

// Mirrors the sidebar structure from the redesign brief. `comingSoon: true`
// routes still navigate (to a real placeholder page) — nothing here 404s.
const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/app-health", label: "App Health", icon: HeartPulse, comingSoon: true },
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3, comingSoon: true },
    ],
  },
  {
    label: "App Control",
    items: [
      { to: "/admin/feature-flags", label: "Feature Flags", icon: Flag, comingSoon: true },
      { to: "/admin/remote-config", label: "Remote Config", icon: SlidersHorizontal, comingSoon: true },
      { to: "/admin/banlists", label: "Banlists", icon: ShieldHalf },
      { to: "/admin/genesys", label: "GENESYS Points", icon: Sparkles },
      { to: "/admin/tag-rules", label: "Card Tag Rules", icon: Tags },
    ],
  },
  {
    label: "Moderation / Review",
    items: [
      { to: "/admin/tag-review", label: "AI Tag Review Queue", icon: Inbox },
      { to: "/admin/error-log", label: "Error Log", icon: AlertOctagon },
    ],
  },
  {
    label: "Users",
    items: [
      { to: "/admin/users", label: "Users", icon: Users, comingSoon: true },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/achievements", label: "Achievements", icon: Trophy, comingSoon: true },
      { to: "/admin/announcements", label: "App Announcements", icon: Megaphone, comingSoon: true },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
      { to: "/admin/settings", label: "Settings", icon: Settings, comingSoon: true },
    ],
  },
  {
    label: "Reference",
    items: [
      { to: "/admin/docs", label: "Documentation", icon: BookOpen },
    ],
  },
];

const AdminSidebar = ({ onNavigate, onClose }) => {
  return (
    <nav className="h-full flex flex-col bg-surface/60 border-r border-celestial-border/15">
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-glow-light/15 flex items-center justify-center text-glow-light font-display font-bold text-sm">
            D
          </span>
          <span className="font-display font-bold text-celestial-text text-sm tracking-wide">
            Deck Ratio Admin
          </span>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden text-celestial-muted hover:text-celestial-text p-1"
          aria-label="Close sidebar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-5">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-celestial-muted/60">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {group.items.map(({ to, label, icon: Icon, comingSoon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? "bg-glow-mid/15 text-celestial-text font-medium"
                        : "text-celestial-muted hover:text-celestial-text hover:bg-surface/60"
                    }`
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate flex-1">{label}</span>
                  {comingSoon && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-surface/80 text-celestial-muted/70 shrink-0">
                      soon
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </div>
    </nav>
  );
};

export default AdminSidebar;
